import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Download,
    AlertTriangle,
    ArrowUpRight,
    ArrowDownRight,
    Minus,
    RefreshCw,
    BarChart3,
    Table2,
    ChevronDown,
    ChevronUp,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import axiosInstance from '../../utils/axiosConfig';
import { formatoMonto } from '../../utils/menuComida';

const API = '/api/comidas/dashboard';

// Un solo azul para todo lo que es "cuánto se gastó"; la pista de las barras es un
// paso claro del mismo azul. Grilla y ejes, grises recesivos.
const AZUL = '#0062A5';
const AZUL_CLARO = '#8ab8e0';
const AZUL_OSCURO = '#003366';
const PISTA = '#e8f0f8';
const GRILLA = '#f1f5f9';
const EJE = '#94a3b8';

// Los turnos tienen orden (desayuno → cena): escala de un solo azul, de claro a oscuro.
// Validada con el validador de la guía de visualización (modo ordinal, fondo blanco).
// El refrigerio (eventos) no es un turno del día: va en su propio color, el rosa
// con que se marca en la bandeja y el menú.
const ROSA_REFRIGERIO = '#e11d48';
const COLOR_TURNO = { Desayuno: AZUL_CLARO, Almuerzo: AZUL, Cena: AZUL_OSCURO, Refrigerio: ROSA_REFRIGERIO };
// El delivery es contexto del gasto en comida: gris, para no confundirse con un turno.
const GRIS_DELIVERY = '#94a3b8';

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// Fechas locales en YYYY-MM-DD (toISOString es UTC y corre el día de noche).
const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const desdeIso = (iso) => {
    const [a, m, d] = iso.split('-').map(Number);
    return new Date(a, m - 1, d);
};

const RANGOS = [
    { id: 'mes', label: 'Este mes' },
    { id: 'mes_anterior', label: 'Mes anterior' },
    { id: '3m', label: '3 meses' },
    { id: 'anio', label: 'Este año' },
];

const rangoDe = (id) => {
    const hoy = new Date();
    const hasta = isoLocal(hoy);
    if (id === 'mes_anterior') {
        return {
            desde: isoLocal(new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1)),
            hasta: isoLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 0)),
        };
    }
    if (id === '3m') return { desde: isoLocal(new Date(hoy.getFullYear(), hoy.getMonth() - 2, 1)), hasta };
    if (id === 'anio') return { desde: isoLocal(new Date(hoy.getFullYear(), 0, 1)), hasta };
    return { desde: isoLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), hasta };
};

// ── Período de comparación ──
// Los rangos por mes se comparan con el mismo tramo corrido N meses atrás (1-8 oct
// contra 1-8 sep; septiembre completo contra agosto completo; el año contra el mismo
// tramo del año pasado). Un rango a mano, con los N días justo anteriores.
const ultimoDia = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
const sumarMeses = (d, k) => {
    const destino = new Date(d.getFullYear(), d.getMonth() + k, 1);
    const dia = d.getDate() === ultimoDia(d) ? ultimoDia(destino) : Math.min(d.getDate(), ultimoDia(destino));
    return new Date(destino.getFullYear(), destino.getMonth(), dia);
};
const MESES_DEL_RANGO = { mes: 1, mes_anterior: 1, '3m': 3, anio: 12 };
const periodoAnterior = (rango, { desde, hasta }) => {
    const d = desdeIso(desde);
    const h = desdeIso(hasta);
    const meses = MESES_DEL_RANGO[rango];
    if (meses) return { desde: isoLocal(sumarMeses(d, -meses)), hasta: isoLocal(sumarMeses(h, -meses)) };
    const dias = Math.round((h - d) / 86400000) + 1;
    const hastaPrevio = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);
    const desdePrevio = new Date(hastaPrevio.getFullYear(), hastaPrevio.getMonth(), hastaPrevio.getDate() - (dias - 1));
    return { desde: isoLocal(desdePrevio), hasta: isoLocal(hastaPrevio) };
};

// "1 – 8 oct 2026", "18 sep – 8 oct 2026", "8 oct 2026"
const textoRango = ({ desde, hasta }) => {
    if (!desde || !hasta) return '';
    const d = desdeIso(desde);
    const h = desdeIso(hasta);
    const corta = (f) => `${f.getDate()} ${MESES_CORTOS[f.getMonth()]}`;
    if (desde === hasta) return `${corta(h)} ${h.getFullYear()}`;
    if (d.getFullYear() !== h.getFullYear()) return `${corta(d)} ${d.getFullYear()} – ${corta(h)} ${h.getFullYear()}`;
    if (d.getMonth() === h.getMonth()) return `${d.getDate()} – ${corta(h)} ${h.getFullYear()}`;
    return `${corta(d)} – ${corta(h)} ${h.getFullYear()}`;
};

const numero = (v) => Number(v || 0).toLocaleString('es-VE');
const montoEje = (v) => `$ ${Number(v || 0).toLocaleString('es-VE', { maximumFractionDigits: 0 })}`;
const porcentaje = (parte, total) => {
    if (!(total > 0)) return '—';
    const pct = (parte / total) * 100;
    return pct > 0 && pct < 1 ? '<1%' : `${Math.round(pct)}%`;
};
const plural = (n, uno, varios) => `${numero(n)} ${n === 1 ? uno : varios}`;

const descargarBlob = (data, nombre) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = nombre;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

// Mismo departamento escrito distinto (el Excel histórico viene en mayúsculas:
// "SERVICIOS GENERALES" y "Servicios Generales") se muestra en una sola fila.
const claveNombre = (t) => (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
const unirDepartamentos = (lista) => {
    const grupos = new Map();
    lista.forEach(d => {
        const nombre = d.departamento || 'Sin departamento';
        const clave = claveNombre(nombre);
        const actual = grupos.get(clave);
        if (!actual) {
            grupos.set(clave, { ...d, departamento: nombre });
            return;
        }
        actual.monto = (actual.monto || 0) + (d.monto || 0);
        actual.pedidos = (actual.pedidos || 0) + (d.pedidos || 0);
        actual.delivery = (actual.delivery || 0) + (d.delivery || 0);
        // Se prefiere el nombre como lo escribe el portal, no en mayúsculas sostenidas
        if (actual.departamento === actual.departamento.toUpperCase() && nombre !== nombre.toUpperCase()) {
            actual.departamento = nombre;
        }
        actual.centro_costo = actual.centro_costo || d.centro_costo;
    });
    return [...grupos.values()].sort((a, b) => (b.monto || 0) - (a.monto || 0) || (b.pedidos || 0) - (a.pedidos || 0));
};

// ── Piezas ──

const Tarjeta = ({ titulo, nota, extra, children, className = '' }) => (
    <section className={`bg-white rounded-2xl border border-gray-100 ${className}`}>
        <header className="px-5 pt-5 pb-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
                <h3 className="text-[15px] font-semibold text-gray-900">{titulo}</h3>
                {nota && <p className="text-xs text-gray-400 mt-0.5">{nota}</p>}
            </div>
            {extra}
        </header>
        {children}
    </section>
);

// Una fila con su barra: el valor va escrito, la barra solo da la proporción.
const FilaBarra = ({ titulo, etiqueta, valor, detalle, proporcion }) => (
    <li className="px-5 py-2.5 hover:bg-gray-50/70 transition-colors">
        <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex items-start gap-2">
                {etiqueta}
                <span className="text-sm text-gray-800 leading-snug">{titulo}</span>
            </div>
            <div className="text-right shrink-0">
                <p className="text-sm font-semibold text-gray-900 tabular-nums">{valor}</p>
                {detalle && <p className="text-[11px] text-gray-400 tabular-nums">{detalle}</p>}
            </div>
        </div>
        <div className="mt-1.5 h-1.5 rounded-full overflow-hidden" style={{ background: PISTA }}>
            <div className="h-full rounded-full" style={{ width: `${proporcion > 0 ? Math.max(proporcion * 100, 1.5) : 0}%`, background: AZUL }} />
        </div>
    </li>
);

const VerTodos = ({ total, visibles, abierto, onClick }) => (
    total > visibles || abierto ? (
        <button type="button" onClick={onClick}
            className="w-full flex items-center justify-center gap-1 px-5 py-2.5 border-t border-gray-100 text-xs font-medium text-gray-500 hover:text-sura-blue hover:bg-gray-50/70 transition-colors rounded-b-2xl">
            {abierto ? <>Ver menos <ChevronUp size={14} /></> : <>Ver todos ({total}) <ChevronDown size={14} /></>}
        </button>
    ) : null
);

const Indicador = ({ label, valor, detalle, alerta }) => (
    <div className="px-5 py-4 min-w-0">
        <p className="text-xs text-gray-500 flex items-center gap-1">
            {alerta && <AlertTriangle size={12} className="text-amber-600 shrink-0" />}
            {label}
        </p>
        <p className={`mt-1 text-xl sm:text-2xl font-semibold leading-tight ${alerta ? 'text-amber-700' : 'text-gray-900'}`}>{valor}</p>
        {detalle && <p className="mt-0.5 text-[11px] text-gray-400">{detalle}</p>}
    </div>
);

// Tooltip: el monto manda, la fecha y los pedidos acompañan.
const TooltipEvolucion = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-3 py-2">
            <p className="text-sm font-semibold text-gray-900 tabular-nums">{formatoMonto(d.monto)}</p>
            <p className="text-[11px] text-gray-500">{d.largo} · {plural(d.pedidos, 'pedido', 'pedidos')}</p>
        </div>
    );
};

const Esqueleto = () => (
    <div className="space-y-6">
        <div className="skeleton h-44 rounded-2xl bg-gray-100" />
        <div className="skeleton h-72 rounded-2xl bg-gray-100" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="skeleton h-64 rounded-2xl bg-gray-100" />
            <div className="skeleton h-64 rounded-2xl bg-gray-100" />
        </div>
    </div>
);

const MAX_FILAS = 8;

const ComidaDashboard = () => {
    const [rango, setRango] = useState('mes');
    const [fechas, setFechas] = useState(() => rangoDe('mes'));
    const [datos, setDatos] = useState(null);
    const [previo, setPrevio] = useState(null); // { desde, hasta, totales } del período de comparación
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState('');
    const [exportando, setExportando] = useState(false);
    const [vistaEvolucion, setVistaEvolucion] = useState('grafico');
    const [todosDeptos, setTodosDeptos] = useState(false);
    const [todosCentros, setTodosCentros] = useState(false);
    const pedido = useRef(0);

    const cargar = useCallback(async () => {
        if (!fechas.desde || !fechas.hasta || fechas.desde > fechas.hasta) return;
        const id = ++pedido.current;
        setCargando(true);
        setError('');
        const anterior = periodoAnterior(rango, fechas);
        const [actual, comparado] = await Promise.allSettled([
            axiosInstance.get(API, { params: fechas }),
            axiosInstance.get(API, { params: anterior }),
        ]);
        if (id !== pedido.current) return; // llegó tarde: ya se pidió otro rango
        if (actual.status === 'fulfilled') {
            setDatos(actual.value.data);
            setPrevio(comparado.status === 'fulfilled' ? { ...anterior, totales: comparado.value.data?.totales || {} } : null);
        } else {
            setError(actual.reason?.response?.data?.error || 'No se pudo cargar el dashboard');
        }
        setCargando(false);
    }, [fechas, rango]);

    useEffect(() => { cargar(); }, [cargar]);

    const elegirRango = (id) => {
        setRango(id);
        setFechas(rangoDe(id));
    };

    const cambiarFecha = (campo, valor) => {
        if (!valor) return;
        setRango('custom');
        setFechas(prev => ({ ...prev, [campo]: valor }));
    };

    const exportar = async () => {
        setExportando(true);
        try {
            const res = await axiosInstance.get(`${API}/excel`, { params: fechas, responseType: 'blob' });
            descargarBlob(res.data, `Gastos_Comida_${fechas.desde}_${fechas.hasta}.xlsx`);
        } catch {
            toast.error('No se pudo exportar el Excel');
        } finally {
            setExportando(false);
        }
    };

    const totales = datos?.totales || {};
    const pedidos = totales.pedidos || 0;
    const gasto = totales.monto || 0;
    const delivery = totales.delivery || 0;
    const comida = Math.max(gasto - delivery, 0);
    const sinDatos = datos && pedidos === 0 && !totales.deliveries;

    const comparacion = useMemo(() => {
        const antes = previo?.totales?.monto || 0;
        if (!previo || antes <= 0) return null;
        return { antes, cambio: ((gasto - antes) / antes) * 100, texto: textoRango(previo) };
    }, [previo, gasto]);

    // Hasta ~2 meses: un punto por día (los días sin pedidos en 0). Más largo: por mes.
    const evolucion = useMemo(() => {
        if (!datos || !fechas.desde || !fechas.hasta) return { porMes: false, filas: [] };
        const porFecha = new Map((datos.por_dia || []).map(d => [d.fecha, d]));
        const inicio = desdeIso(fechas.desde);
        const fin = desdeIso(fechas.hasta);
        const dias = Math.round((fin - inicio) / 86400000) + 1;
        if (dias <= 0) return { porMes: false, filas: [] };

        if (dias <= 62) {
            const filas = [];
            for (let d = new Date(inicio); d <= fin; d.setDate(d.getDate() + 1)) {
                const iso = isoLocal(d);
                const dato = porFecha.get(iso);
                filas.push({
                    clave: iso,
                    nombre: `${d.getDate()} ${MESES_CORTOS[d.getMonth()]}`,
                    largo: `${d.getDate()} ${MESES_CORTOS[d.getMonth()]} ${d.getFullYear()}`,
                    monto: dato?.monto || 0,
                    pedidos: dato?.pedidos || 0,
                });
            }
            return { porMes: false, filas };
        }

        const meses = new Map();
        for (let d = new Date(inicio.getFullYear(), inicio.getMonth(), 1); d <= fin; d.setMonth(d.getMonth() + 1)) {
            const clave = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const nombre = `${MESES_CORTOS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
            meses.set(clave, { clave, nombre, largo: `${MESES_CORTOS[d.getMonth()]} ${d.getFullYear()}`, monto: 0, pedidos: 0 });
        }
        (datos.por_dia || []).forEach(d => {
            const fila = meses.get((d.fecha || '').slice(0, 7));
            if (fila) {
                fila.monto += d.monto || 0;
                fila.pedidos += d.pedidos || 0;
            }
        });
        return { porMes: true, filas: [...meses.values()] };
    }, [datos, fechas.desde, fechas.hasta]);

    const conGasto = evolucion.filas.filter(f => f.monto > 0 || f.pedidos > 0);
    const pico = conGasto.reduce((max, f) => (!max || f.monto > max.monto ? f : max), null);
    const promedioPeriodo = conGasto.length ? conGasto.reduce((t, f) => t + f.monto, 0) / conGasto.length : 0;

    const nombresCentro = useMemo(
        () => new Map((datos?.por_centro_costo || []).filter(c => c.centro_costo).map(c => [c.centro_costo, c.nombre])),
        [datos]
    );
    const departamentos = useMemo(() => unirDepartamentos(datos?.por_departamento || []), [datos]);
    const centros = datos?.por_centro_costo || [];
    const mayorDepto = Math.max(0, ...departamentos.map(d => d.monto || 0));
    const mayorCentro = Math.max(0, ...centros.map(c => c.monto || 0));
    const totalCentros = centros.reduce((t, c) => t + (c.monto || 0), 0);
    const totalDeptos = departamentos.reduce((t, d) => t + (d.monto || 0), 0);

    const porTurno = datos?.por_turno || [];
    const totalTurnos = porTurno.reduce((t, x) => t + (x.monto || 0), 0);
    const topPlatos = datos?.top_platos || [];
    const mayorPlato = Math.max(0, ...topPlatos.map(p => p.cantidad || 0));

    const CambioIcono = !comparacion ? null
        : Math.abs(comparacion.cambio) < 0.5 ? Minus
            : comparacion.cambio > 0 ? ArrowUpRight : ArrowDownRight;

    return (
        <div className="space-y-6">
            {/* Período: una sola fila arriba de todo lo que filtra */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1 p-1 bg-white border border-gray-100 rounded-2xl flex-wrap">
                    {RANGOS.map(r => (
                        <button
                            key={r.id}
                            type="button"
                            onClick={() => elegirRango(r.id)}
                            className={`px-3.5 py-2 rounded-xl text-sm font-medium transition-colors ${rango === r.id ? 'bg-sura-blue text-white' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
                        >
                            {r.label}
                        </button>
                    ))}
                    <div className={`flex items-center gap-1 pl-2 ml-1 border-l border-gray-100 text-sm ${rango === 'custom' ? 'text-gray-900' : 'text-gray-400'}`}>
                        <input
                            type="date"
                            aria-label="Desde"
                            value={fechas.desde}
                            max={fechas.hasta}
                            onChange={e => cambiarFecha('desde', e.target.value)}
                            className={`px-2 py-1.5 rounded-lg bg-transparent border border-transparent hover:border-gray-200 focus:border-gray-200 focus:bg-gray-50 outline-none ${rango === 'custom' ? 'font-semibold' : ''}`}
                        />
                        <span>a</span>
                        <input
                            type="date"
                            aria-label="Hasta"
                            value={fechas.hasta}
                            min={fechas.desde}
                            onChange={e => cambiarFecha('hasta', e.target.value)}
                            className={`px-2 py-1.5 rounded-lg bg-transparent border border-transparent hover:border-gray-200 focus:border-gray-200 focus:bg-gray-50 outline-none ${rango === 'custom' ? 'font-semibold' : ''}`}
                        />
                    </div>
                </div>
                <div className="ml-auto flex items-center gap-3">
                    {cargando && datos && <RefreshCw size={14} className="animate-spin text-gray-300" />}
                    <button
                        type="button"
                        onClick={exportar}
                        disabled={exportando || cargando || !pedidos}
                        className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-800 hover:border-sura-blue hover:text-sura-blue transition-colors disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:text-gray-800"
                    >
                        <Download size={16} />
                        {exportando ? 'Generando…' : 'Exportar Excel'}
                    </button>
                </div>
            </div>

            {!datos && cargando ? <Esqueleto /> : error && !datos ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                    <p className="text-gray-500">{error}</p>
                    <button type="button" onClick={cargar} className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-sura-blue hover:underline">
                        <RefreshCw size={14} /> Reintentar
                    </button>
                </div>
            ) : sinDatos ? (
                <div className={`text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200 transition-opacity ${cargando ? 'opacity-50' : ''}`}>
                    <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <BarChart3 size={28} className="text-gray-300" />
                    </div>
                    <p className="text-gray-700 font-medium">Sin gastos de comida del {textoRango(fechas)}</p>
                    <p className="text-gray-400 text-sm mt-1">Prueba con otro período, por ejemplo el mes anterior</p>
                </div>
            ) : datos && (
                // Al cambiar de período se queda lo anterior atenuado: sin saltos ni parpadeos
                <div className={`space-y-6 transition-opacity duration-200 ${cargando ? 'opacity-50 pointer-events-none' : ''}`}>
                    {error && (
                        <p className="px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-sm text-red-700">{error}</p>
                    )}

                    {/* Resumen: el gasto total manda; debajo, los indicadores de apoyo */}
                    <section className="bg-white rounded-2xl border border-gray-100">
                        <div className="p-6 lg:p-7 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-500">Gasto total</p>
                                <p className="mt-1 text-4xl sm:text-5xl font-semibold tracking-tight text-gray-900">{formatoMonto(gasto)}</p>
                                <p className="mt-2 text-sm text-gray-400">USD sin IVA · {textoRango(fechas)}</p>
                                {comparacion && (
                                    <p className="mt-3 flex items-center gap-1.5 flex-wrap text-sm">
                                        <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-lg bg-gray-100 font-semibold text-gray-800">
                                            <CambioIcono size={14} />
                                            {Math.abs(Math.round(comparacion.cambio))}%
                                        </span>
                                        <span className="text-gray-500">vs. {comparacion.texto} ({formatoMonto(comparacion.antes)})</span>
                                    </p>
                                )}
                            </div>

                            {delivery > 0 && (
                                <div className="w-full lg:w-[380px] shrink-0">
                                    {/* Composición: comida y delivery, con 2 px de aire entre los tramos */}
                                    <div className="flex h-2 gap-[2px]" role="img" aria-label={`Comida ${formatoMonto(comida)}, delivery ${formatoMonto(delivery)}`}>
                                        {comida > 0 && <div className="h-full rounded-l-full" style={{ width: `${(comida / gasto) * 100}%`, background: AZUL }} />}
                                        <div className={`h-full rounded-r-full ${comida > 0 ? '' : 'rounded-l-full'}`} style={{ width: `${(delivery / gasto) * 100}%`, background: GRIS_DELIVERY }} />
                                    </div>
                                    <div className="mt-2.5 flex items-center justify-between gap-4 flex-wrap text-xs text-gray-500">
                                        <span className="inline-flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-[3px]" style={{ background: AZUL }} />
                                            Comida <span className="font-semibold text-gray-800 tabular-nums">{formatoMonto(comida)}</span>
                                        </span>
                                        <span className="inline-flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-[3px]" style={{ background: GRIS_DELIVERY }} />
                                            Delivery <span className="font-semibold text-gray-800 tabular-nums">{formatoMonto(delivery)}</span>
                                            <span className="text-gray-400">({plural(totales.deliveries || 0, 'delivery', 'deliveries')})</span>
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-3 border-t border-gray-100 divide-x divide-gray-100">
                            <Indicador
                                label="Pedidos"
                                valor={numero(pedidos)}
                                detalle={totales.importados > 0 ? `${numero(totales.importados)} del Excel histórico de Procura` : null}
                            />
                            <Indicador label="Promedio por pedido" valor={formatoMonto(pedidos ? comida / pedidos : 0)} detalle="Solo comida, sin delivery" />
                            <Indicador
                                label="Pedidos sin precio"
                                valor={numero(totales.sin_precio)}
                                alerta={totales.sin_precio > 0}
                                detalle={totales.sin_precio > 0 ? 'No suman todo lo que costaron: ponles precio en Menú' : 'Todos tienen precio'}
                            />
                        </div>
                    </section>

                    {/* Evolución en el tiempo */}
                    <Tarjeta
                        titulo={evolucion.porMes ? 'Gasto por mes' : 'Gasto por día'}
                        nota={pico ? `Promedio ${formatoMonto(promedioPeriodo)} por ${evolucion.porMes ? 'mes' : 'día'} con pedidos · Más alto: ${pico.largo} (${formatoMonto(pico.monto)})` : null}
                        extra={conGasto.length > 0 && (
                            <div className="flex items-center gap-0.5 p-0.5 bg-gray-100 rounded-lg shrink-0" role="tablist" aria-label="Vista">
                                {[{ id: 'grafico', label: 'Gráfico', icon: BarChart3 }, { id: 'tabla', label: 'Tabla', icon: Table2 }].map(v => (
                                    <button key={v.id} type="button" role="tab" aria-selected={vistaEvolucion === v.id}
                                        onClick={() => setVistaEvolucion(v.id)}
                                        className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${vistaEvolucion === v.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                                        <v.icon size={13} /> {v.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    >
                        {conGasto.length === 0 ? (
                            <p className="px-5 pb-8 pt-4 text-sm text-gray-400 text-center">Sin gastos en este período</p>
                        ) : vistaEvolucion === 'tabla' ? (
                            <div className="px-5 pb-5">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                                            <th className="py-2 font-medium">{evolucion.porMes ? 'Mes' : 'Día'}</th>
                                            <th className="py-2 font-medium text-right">Pedidos</th>
                                            <th className="py-2 font-medium text-right">Gasto</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {conGasto.map(f => (
                                            <tr key={f.clave}>
                                                <td className="py-2 text-gray-700">{f.largo}</td>
                                                <td className="py-2 text-right tabular-nums text-gray-500">{numero(f.pedidos)}</td>
                                                <td className="py-2 text-right tabular-nums font-semibold text-gray-900">{formatoMonto(f.monto)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="border-t border-gray-200">
                                            <td className="py-2 font-semibold text-gray-900">Total</td>
                                            <td className="py-2 text-right tabular-nums font-semibold text-gray-900">{numero(conGasto.reduce((t, f) => t + f.pedidos, 0))}</td>
                                            <td className="py-2 text-right tabular-nums font-semibold text-gray-900">{formatoMonto(conGasto.reduce((t, f) => t + f.monto, 0))}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                                {!evolucion.porMes && conGasto.length < evolucion.filas.length && (
                                    <p className="mt-2 text-[11px] text-gray-400">Los días sin pedidos no se listan.</p>
                                )}
                            </div>
                        ) : (
                            <div className="px-3 pb-4" role="img"
                                aria-label={`Gasto por ${evolucion.porMes ? 'mes' : 'día'}. Más alto: ${pico?.largo}, ${formatoMonto(pico?.monto)}. El detalle está en la vista Tabla.`}>
                                <ResponsiveContainer width="100%" height={250}>
                                    {/* Columnas: cada día (o mes) es un monto propio; una curva inventaría valores entre días */}
                                    <BarChart data={evolucion.filas} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                                        <CartesianGrid vertical={false} stroke={GRILLA} />
                                        <XAxis dataKey="nombre" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: EJE }} minTickGap={18} />
                                        <YAxis tickFormatter={montoEje} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: EJE }} width={68} />
                                        <Tooltip cursor={{ fill: '#f1f5f9' }} content={<TooltipEvolucion />} />
                                        <Bar dataKey="monto" fill={AZUL} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </Tarjeta>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                        {/* Departamentos */}
                        <Tarjeta titulo="Gasto por departamento" nota={plural(departamentos.length, 'departamento', 'departamentos')}>
                            {departamentos.length === 0 ? (
                                <p className="px-5 pb-8 pt-4 text-sm text-gray-400 text-center">Sin datos en este período</p>
                            ) : (
                                <>
                                    <ul className="pb-2">
                                        {(todosDeptos ? departamentos : departamentos.slice(0, MAX_FILAS)).map(d => (
                                            <FilaBarra
                                                key={d.departamento}
                                                titulo={d.departamento}
                                                etiqueta={d.centro_costo && (
                                                    <span className="mt-0.5 shrink-0 px-1.5 py-px rounded-md bg-gray-100 text-[11px] font-mono text-gray-500"
                                                        title={nombresCentro.get(d.centro_costo) ? `Centro de costo ${d.centro_costo} · ${nombresCentro.get(d.centro_costo)}` : `Centro de costo ${d.centro_costo}`}>
                                                        {d.centro_costo}
                                                    </span>
                                                )}
                                                valor={formatoMonto(d.monto)}
                                                detalle={`${porcentaje(d.monto || 0, totalDeptos)} · ${plural(d.pedidos || 0, 'pedido', 'pedidos')}`}
                                                proporcion={mayorDepto ? (d.monto || 0) / mayorDepto : 0}
                                            />
                                        ))}
                                    </ul>
                                    <VerTodos total={departamentos.length} visibles={MAX_FILAS} abierto={todosDeptos} onClick={() => setTodosDeptos(v => !v)} />
                                </>
                            )}
                        </Tarjeta>

                        {/* Centros de costo */}
                        <Tarjeta titulo="Gasto por centro de costo" nota={plural(centros.length, 'centro de costo', 'centros de costo')}>
                            {centros.length === 0 ? (
                                <p className="px-5 pb-8 pt-4 text-sm text-gray-400 text-center">Sin datos en este período</p>
                            ) : (
                                <>
                                    <ul className="pb-2">
                                        {(todosCentros ? centros : centros.slice(0, MAX_FILAS)).map(c => (
                                            <FilaBarra
                                                key={c.centro_costo || 'sin'}
                                                titulo={c.nombre || 'Sin centro de costo'}
                                                etiqueta={(
                                                    <span className="mt-0.5 shrink-0 px-1.5 py-px rounded-md bg-gray-100 text-[11px] font-mono text-gray-500">
                                                        {c.centro_costo || '—'}
                                                    </span>
                                                )}
                                                valor={formatoMonto(c.monto)}
                                                detalle={`${porcentaje(c.monto || 0, totalCentros)} · ${plural(c.pedidos || 0, 'pedido', 'pedidos')}`}
                                                proporcion={mayorCentro ? (c.monto || 0) / mayorCentro : 0}
                                            />
                                        ))}
                                    </ul>
                                    <VerTodos total={centros.length} visibles={MAX_FILAS} abierto={todosCentros} onClick={() => setTodosCentros(v => !v)} />
                                </>
                            )}
                        </Tarjeta>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                        {/* Turnos: cuánto se lleva cada uno del total */}
                        <Tarjeta titulo="Gasto por turno" nota="Parte del gasto total que se lleva cada turno">
                            {totalTurnos <= 0 ? (
                                <p className="px-5 pb-8 pt-4 text-sm text-gray-400 text-center">Sin datos en este período</p>
                            ) : (
                                <div className="px-5 pb-5">
                                    <div className="flex h-3 gap-[2px]" role="img"
                                        aria-label={porTurno.map(t => `${t.turno} ${porcentaje(t.monto || 0, totalTurnos)}`).join(', ')}>
                                        {porTurno.filter(t => (t.monto || 0) > 0).map(t => (
                                            <div key={t.turno} className="h-full first:rounded-l-full last:rounded-r-full"
                                                title={`${t.turno}: ${formatoMonto(t.monto)} (${porcentaje(t.monto, totalTurnos)})`}
                                                style={{ width: `${(t.monto / totalTurnos) * 100}%`, background: COLOR_TURNO[t.turno] || AZUL }} />
                                        ))}
                                    </div>
                                    <ul className="mt-4 divide-y divide-gray-50">
                                        {porTurno.map(t => (
                                            <li key={t.turno} className="py-2.5 flex items-start justify-between gap-4">
                                                <span className="inline-flex items-center gap-2 text-sm text-gray-800">
                                                    <span className="w-2.5 h-2.5 rounded-[3px] shrink-0" style={{ background: COLOR_TURNO[t.turno] || AZUL }} />
                                                    {t.turno}
                                                </span>
                                                <span className="text-right">
                                                    <span className="block text-sm font-semibold text-gray-900 tabular-nums">
                                                        {formatoMonto(t.monto)}
                                                        <span className="ml-2 font-normal text-gray-400">{porcentaje(t.monto || 0, totalTurnos)}</span>
                                                    </span>
                                                    <span className="block text-[11px] text-gray-400 tabular-nums">
                                                        {plural(t.pedidos || 0, 'pedido', 'pedidos')}
                                                        {t.delivery > 0 && <> · delivery {formatoMonto(t.delivery)}</>}
                                                    </span>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </Tarjeta>

                        {/* Platos más pedidos */}
                        <Tarjeta titulo="Platos más pedidos" nota="Solo pedidos hechos en el portal">
                            {topPlatos.length === 0 ? (
                                <p className="px-5 pb-8 pt-4 text-sm text-gray-400 text-center">Sin pedidos del portal en este período</p>
                            ) : (
                                <ol className="pb-2">
                                    {topPlatos.map((p, i) => (
                                        <FilaBarra
                                            key={`${p.nombre}-${i}`}
                                            titulo={p.nombre}
                                            etiqueta={<span className="w-5 shrink-0 text-xs text-gray-400 tabular-nums text-right mt-px">{i + 1}</span>}
                                            valor={plural(p.cantidad || 0, 'pedido', 'pedidos')}
                                            detalle={p.monto > 0 ? formatoMonto(p.monto) : 'Sin precio'}
                                            proporcion={mayorPlato ? (p.cantidad || 0) / mayorPlato : 0}
                                        />
                                    ))}
                                </ol>
                            )}
                        </Tarjeta>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ComidaDashboard;
